import React, { useState, useEffect } from "react";
import axios from "axios";
import { Editor } from "@monaco-editor/react";

const defaultApis = [
  {
    name: "Get Users",
    method: "GET",
    url: "http://localhost:8000/api/users",
    body: "",
    headers: `{
      "Authorization": "Bearer your-token-here"
    }`
  },
  {
    name: "Create User",
    method: "POST",
    url: "http://localhost:8000/api/users",
    body: `{
  "name": "John",
  "email": "john@example.com"
}`,
    headers: `{
     "Authorization": "Bearer your-token-here"
     }`
  }
];

const LOCAL_STORAGE_KEY = "miniPostmanApis";

const MiniPostmanApp = () => {
  const [apis, setApis] = useState(defaultApis);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [responseBody, setResponseBody] = useState<string | null>(null);
  const [statusCode, setStatusCode] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [apiResponses, setApiResponses] = useState<{ statusCode: number | null, body: string | null }[]>([]);

  // Load from localStorage on mount
  useEffect(() => {
    const savedApis = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (savedApis) {
      const parsedApis = JSON.parse(savedApis);
      setApis(parsedApis);
      setApiResponses(parsedApis.map(() => ({ statusCode: null, body: null })));
      setSelectedIndex(0);
    } else {
      setApiResponses(defaultApis.map(() => ({ statusCode: null, body: null })));
    }
  }, []);

  // Save to localStorage on API list change
  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(apis));
  }, [apis]);

  const selectedApi = apis[selectedIndex];

  const sendRequest = async () => {
    setLoading(true);
    const currentIndex = selectedIndex;
    const updatedResponses = [...apiResponses];
    updatedResponses[currentIndex] = { statusCode: null, body: null };
    setApiResponses(updatedResponses);

    try {
      let parsedHeaders = {};
      try {
        parsedHeaders = selectedApi.headers ? JSON.parse(selectedApi.headers) : {};
      } catch (err) {
        updatedResponses[currentIndex] = {
          statusCode: 400,
          body: JSON.stringify({ error: "Invalid JSON in headers" }, null, 2)
        };
        setApiResponses(updatedResponses);
        setLoading(false);
        return;
      }

      const options: any = {
        method: selectedApi.method,
        url: selectedApi.url,
        headers: {
          "Content-Type": "application/json",
          ...parsedHeaders,
        },
      };

      if (selectedApi.method !== "GET" && selectedApi.method !== "DELETE") {
        options.data = JSON.parse(selectedApi.body || "{}");
      }

      const res = await axios(options);
      updatedResponses[currentIndex] = {
        statusCode: res.status,
        body: JSON.stringify(res.data, null, 2)
      };
    } catch (err: any) {
      updatedResponses[currentIndex] = {
        statusCode: err.response?.status || 500,
        body: JSON.stringify(err.response?.data || { error: err.message }, null, 2)
      };
    } finally {
      setApiResponses(updatedResponses);
      setLoading(false);
    }
  };

  const handleRemoveApi = (indexToRemove: number) => {
    const updatedApis = apis.filter((_, i) => i !== indexToRemove);
    const updatedResponses = apiResponses.filter((_, i) => i !== indexToRemove);
  
    setApis(updatedApis);
    setApiResponses(updatedResponses);
  
    // Update selected index safely
    if (selectedIndex >= updatedApis.length) {
      setSelectedIndex(Math.max(0, updatedApis.length - 1));
    } else {
      setSelectedIndex(selectedIndex);
    }
  };

  const handleAddApi = () => {
    const newApi = {
      name: "",
      method: "GET",
      url: "http://localhost:8000/api/new",
      body: "",
      headers: `{ "Authorization": "Bearer your-token-here"}`
    };
    const updatedApis = [...apis, newApi];
    setApis(updatedApis);
    setApiResponses([...apiResponses, { statusCode: null, body: null }]);
    setSelectedIndex(updatedApis.length - 1);
  };

  const updateSelectedApi = (updatedApi: any) => {
    const updatedApis = [...apis];
    updatedApis[selectedIndex] = updatedApi;
    setApis(updatedApis);
  };

  return (
    <div className="flex min-h-screen">
      {/* Sidebar */}
      <aside className="w-64 bg-gray-800 text-white p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">🔗 API List</h2>
          <button
            className="bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded text-sm"
            onClick={handleAddApi}
          >
            + Add
          </button>
        </div>
        <ul className="space-y-2">
          {apis.map((api, index) => (
            <li
            key={index}
            className={`group flex items-center justify-between cursor-pointer px-3 py-2 rounded hover:bg-gray-700 ${
              selectedIndex === index ? "bg-gray-700" : ""
            }`}
          >
            <span onClick={() => setSelectedIndex(index)} className="flex-1 truncate">
              {api.name || `Untitled API ${index + 1}`}
            </span>
            <button
              onClick={() => handleRemoveApi(index)}
              className="ml-2 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition"
              title="Remove API"
            >
              ✕
            </button>
          </li>
          ))}
        </ul>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-6 space-y-4 bg-gray-100 text-black">
        <input
          type="text"
          value={selectedApi.name}
          onChange={(e) => updateSelectedApi({ ...selectedApi, name: e.target.value })}
          className="text-xl font-bold border-b border-gray-300 focus:outline-none text-black"
        />
        <div className="flex space-x-2">
          <select
            className="border px-2 py-1 rounded text-sm text-black"
            value={selectedApi.method}
            onChange={(e) => updateSelectedApi({ ...selectedApi, method: e.target.value })}
          >
            <option>GET</option>
            <option>POST</option>
            <option>PUT</option>
            <option>PATCH</option>
            <option>DELETE</option>
          </select>
          <input
            className="flex-1 border px-3 py-1 rounded text-sm text-black"
            placeholder="Enter full API URL"
            value={selectedApi.url}
            onChange={(e) => updateSelectedApi({ ...selectedApi, url: e.target.value })}
          />
          <button
            className="ml-auto bg-blue-600 text-white px-4 py-1 rounded text-sm"
            onClick={sendRequest}
            disabled={loading}
          >
            {loading ? "Sending..." : "Send"}
          </button>
        </div>


        {selectedApi && (
          <div className="mt-4">
            <label className="block text-sm font-semibold mb-1 text-gray-700">
              Request Headers (JSON)
            </label>
            <div className="border rounded overflow-hidden">
              <Editor
                height="120px"
                defaultLanguage="json"
                value={selectedApi.headers}
                onChange={(value: any) => updateSelectedApi({ ...selectedApi, headers: value || "" })}
                theme="vs-light"
                options={{
                  minimap: { enabled: false },
                  fontSize: 14,
                  formatOnPaste: true,
                  formatOnType: true,
                }}
              />
            </div>
          </div>
        )}

        {(selectedApi.method !== "GET" && selectedApi.method !== "DELETE") && (
          <div>
            <label className="block text-sm font-semibold mb-1 text-gray-700">
              JSON Payload
            </label>
            <div className="border rounded overflow-hidden">
              <Editor
                height="200px"
                defaultLanguage="json"
                value={selectedApi.body}
                onChange={(value: any) => updateSelectedApi({ ...selectedApi, body: value || "" })}
                theme="vs-light"
                options={{
                  minimap: { enabled: false },
                  fontSize: 14,
                  formatOnPaste: true,
                  formatOnType: true,
                }}
              />
            </div>
          </div>
        )}



        {apiResponses[selectedIndex]?.statusCode !== null && (
          <div className="text-sm font-semibold text-gray-700">
            HTTP Status:{" "}
            <span
              className={
                apiResponses[selectedIndex]?.statusCode! < 300
                  ? "text-green-600"
                  : "text-red-600"
              }
            >
              {apiResponses[selectedIndex]?.statusCode}
            </span>
          </div>
        )}
        {apiResponses[selectedIndex]?.body && (
          <div>
            <label className="block text-sm font-semibold mb-1 text-gray-700">
              Response Body
            </label>
            <pre className="w-full max-w-[800px] p-3 bg-white border rounded text-sm font-mono max-h-[400px] overflow-auto break-words whitespace-pre-wrap">
              {apiResponses[selectedIndex].body}
            </pre>
          </div>
        )}
        {/* {statusCode !== null && (
          <div className="text-sm font-semibold text-gray-700">
            HTTP Status: <span className={statusCode < 300 ? "text-green-600" : "text-red-600"}>{statusCode}</span>
          </div>
        )}

        {responseBody && (
          <div>
            <label className="block text-sm font-semibold mb-1 text-gray-700">Response Body</label>
            <pre className="w-full  max-w-[1300px] p-3 bg-white border rounded text-sm font-mono max-h-[400px] overflow-auto break-words whitespace-pre-wrap">
              {responseBody}
            </pre>
          </div>
        )} */}
      </main>
    </div>
  );
};

export default MiniPostmanApp;
